import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { openDocument } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ShieldCheck, ShieldOff, BadgeCheck, Check, X as XIcon, Save, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const APPLICANT_VERIFS = ["identity", "income", "employment", "veteran", "referral"];
const PROVIDER_VERIFS = ["facility", "organization", "contact", "program"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [contact, setContact] = useState({});
  const [branding, setBranding] = useState({});
  const [docs, setDocs] = useState([]);
  const [trashedDocs, setTrashedDocs] = useState([]);
  const [kessServices, setKessServices] = useState([]);
  const [kessRequests, setKessRequests] = useState([]);
  const [adminReferrals, setAdminReferrals] = useState([]);
  const [refStatusFilter, setRefStatusFilter] = useState("");
  const [scanData, setScanData] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [intakeDialog, setIntakeDialog] = useState(null);
  const [intakeVerifs, setIntakeVerifs] = useState({});
  const [intakeDetail, setIntakeDetail] = useState(null);
  const [provDialog, setProvDialog] = useState(null);
  const [provVerifs, setProvVerifs] = useState({});
  const [providerApps, setProviderApps] = useState([]);
  const [appStatusFilter, setAppStatusFilter] = useState("submitted");
  const [reviewModal, setReviewModal] = useState(null);
  const [paymentsLedger, setPaymentsLedger] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  // Deep-link from notification dropdown: ?tab=applications&app=<id>.
  // Use react-router's useSearchParams so this effect re-fires on every in-app URL
  // change (not just on initial mount).
  const [activeTab, setActiveTab] = useState("requests");
  const [searchParams] = useSearchParams();

  const openReview = useCallback(async (appId) => {
    try {
      const { data } = await api.get(`/admin/provider-applications/${appId}`);
      setReviewModal(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load application");
    }
  }, []);

  useEffect(() => {
    const t = searchParams.get("tab");
    const app = searchParams.get("app");
    if (t) setActiveTab(t);
    if (app) openReview(app);
  }, [searchParams, openReview]);

  const load = useCallback(async () => {
    const [u, a, i, l, c, b, d, t, ks, kr, ar, pa, pl, al] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/analytics"),
      api.get("/admin/intakes"),
      api.get("/listings", { params: { approved_only: false } }),
      api.get("/settings/contact"),
      api.get("/settings/branding"),
      api.get("/admin/documents"),
      api.get("/admin/documents/trash"),
      api.get("/kess/services"),
      api.get("/kess/requests"),
      api.get("/admin/referrals", { params: refStatusFilter ? { status: refStatusFilter } : {} }),
      api.get("/admin/provider-applications", { params: appStatusFilter ? { status: appStatusFilter } : {} }),
      api.get("/admin/payments"),
      api.get("/admin/audit-logs", { params: { limit: 100 } }),
    ]);
    setUsers(u.data); setAnalytics(a.data); setIntakes(i.data);
    setPendingListings(l.data.filter(x => x.approval_status === "pending"));
    setContact(c.data || {}); setBranding(b.data || {});
    setDocs(d.data); setTrashedDocs(t.data);
    setKessServices(ks.data); setKessRequests(kr.data);
    setAdminReferrals(ar.data);
    setProviderApps(pa.data);
    setPaymentsLedger(pl.data);
    setAuditLogs(al.data);
  }, [refStatusFilter, appStatusFilter]);
  useEffect(() => { load(); }, [load]);

  const approveApp = async (appId) => {
    try {
      await api.post(`/admin/provider-applications/${appId}/approve`);
      toast.success("Application approved");
      setReviewModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  const denyApp = async (appId) => {
    const reason = window.prompt("Reason for denial (required — sent to applicant by email):");
    if (!reason) return;
    try {
      await api.post(`/admin/provider-applications/${appId}/deny`, { reason });
      toast.success("Application denied");
      setReviewModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deny failed");
    }
  };

  const requestMoreInfo = async (appId) => {
    const reason = window.prompt("What information is needed? (sent to applicant by email):");
    if (!reason) return;
    try {
      await api.post(`/admin/provider-applications/${appId}/request-more-info`, { reason });
      toast.success("Applicant has been asked for more info");
      setReviewModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    }
  };

  const downloadDocument = async (docId) => {
    try {
      await openDocument(docId);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const runScan = async () => {
    setScanLoading(true);
    try {
      const { data } = await api.get("/admin/test-data/scan");
      setScanData(data);
      toast.success("Scan complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanLoading(false);
    }
  };

  const executeCleanup = async () => {
    const totalUsers = scanData?.totals?.users?.would_delete || 0;
    const totalListings = scanData?.totals?.listings?.would_delete || 0;
    const totalPayments = scanData?.totals?.payment_transactions?.would_delete || 0;
    const phrase = window.prompt(
      `This will PERMANENTLY DELETE ${totalUsers} test users, ${totalListings} test listings, ${totalPayments} unpaid test payment records, and all related cascade records (referrals, documents, favorites, notifications, intakes, agencies, Kess requests) from the database the API is currently connected to.\n\nProtected emails and listings (Rosehill House, NestBridge Living, Safe Harbor Veterans) will be preserved.\n\nType DELETE (all caps) to confirm:`
    );
    if (phrase !== "DELETE") {
      toast.message("Cleanup cancelled.");
      return;
    }
    setScanLoading(true);
    try {
      const { data } = await api.post(`/admin/test-data/cleanup?confirm=DELETE`);
      setScanData(null);
      const lines = Object.entries(data.deleted || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Cleanup complete — deleted: ${lines}`);
      // Re-scan to confirm no flagged records remain
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setScanLoading(false);
    }
  };

  const overrideReferral = async (id, newStatus) => {
    const reason = prompt(`Override referral to '${newStatus}'? Reason:`);
    if (reason === null) return;
    try {
      await api.patch(`/referrals/${id}`, { status: newStatus, reason });
      toast.success(`Referral set to ${newStatus.replace('_',' ')}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const restoreDoc = async (id) => {
    await api.post(`/admin/documents/${id}/restore`);
    toast.success("Document restored");
    load();
  };
  const updateKessService = async (key, patch) => {
    await api.put(`/kess/services/${key}`, patch);
    toast.success("Service updated");
    load();
  };
  const updateKessRequest = async (id, status, note = "") => {
    await api.patch(`/kess/requests/${id}`, { status, note });
    toast.success(`Request marked ${status.replace('_', ' ')}`);
    load();
  };

  const verifyDoc = async (id) => {
    await api.post(`/admin/documents/${id}/verify`);
    toast.success("Document verified");
    load();
  };
  const rejectDoc = async (id) => {
    const reason = prompt("Reason for rejection?") || "Did not meet requirements";
    await api.post(`/admin/documents/${id}/reject`, { reason });
    toast.success("Document rejected");
    load();
  };

  const verify = async (id, current) => {
    await api.post(`/admin/users/${id}/${current ? "unverify" : "verify"}`);
    toast.success(current ? "Unverified" : "Verified");
    load();
  };

  const approveListing = async (id) => {
    await api.post(`/admin/listings/${id}/approve`);
    toast.success("Listing approved");
    load();
  };
  const denyListing = async (id) => {
    const reason = prompt("Reason for denial?") || "Did not meet guidelines";
    await api.post(`/admin/listings/${id}/deny`, { reason });
    toast.success("Listing denied");
    load();
  };

  const openIntakeVerify = async (intake) => {
    setIntakeDialog(intake);
    setIntakeVerifs(intake.verifications || {});
    setIntakeDetail(null);
    try {
      const { data } = await api.get(`/admin/intakes/${intake.id}/detail`);
      setIntakeDetail(data);
    } catch (e) {
      console.warn("intake detail load failed", e?.response?.status);
    }
  };
  const saveIntakeVerifs = async () => {
    await api.post(`/admin/intakes/${intakeDialog.id}/verify`, { verifications: intakeVerifs });
    toast.success("Applicant verifications updated");
    setIntakeDialog(null); setIntakeDetail(null); load();
  };

  const openProvVerify = (u) => {
    setProvDialog(u);
    setProvVerifs(u.verifications || {});
  };
  const saveProvVerifs = async () => {
    await api.post(`/admin/providers/${provDialog.id}/verifications`, { verifications: provVerifs });
    toast.success("Provider verifications updated");
    setProvDialog(null); load();
  };

  const grantAdmin = async (id) => {
    await api.post(`/admin/grant/${id}`);
    toast.success("Granted admin");
    load();
  };
  const revokeAdmin = async (id) => {
    await api.post(`/admin/revoke/${id}`);
    toast.success("Revoked admin");
    load();
  };

  const saveContact = async () => {
    await api.put("/settings/contact", contact);
    toast.success("Contact settings saved");
  };
  const saveBranding = async () => {
    await api.put("/settings/branding", branding);
    toast.success("Branding settings saved");
  };

  const isSuper = user.role === "super_admin";
  const pendingApplicants = intakes.filter(i => !i.verified_applicant);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
      <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">{isSuper ? "Super Admin Console" : "Admin Console"}</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Safe Harbor Operations</h1>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 mb-10">
          <Stat title="Users" value={analytics.total_users} />
          <Stat title="Providers" value={`${analytics.verified_providers} / ${analytics.providers}`} sub="verified / total" />
          <Stat title="Listings" value={`${analytics.available_listings} / ${analytics.total_listings}`} sub="available / total" />
          <Stat title="Pending Listings" value={analytics.pending_listings} accent />
          <Stat title="Seekers" value={analytics.seekers} />
          <Stat title="Agencies / CMs" value={`${analytics.agencies + analytics.case_managers}`} />
          <Stat title="Placements" value={analytics.placements} accent />
          {isSuper && <Stat title="Revenue" value={`$${(analytics.total_revenue_cents / 100).toFixed(2)}`} accent />}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="requests" data-testid="tab-requests">Requests ({pendingListings.length + pendingApplicants.length})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">Applications ({providerApps.filter(a => a.status === "submitted").length})</TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="applicants" data-testid="tab-applicants">Applicants</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({docs.filter(d => !d.verified).length})</TabsTrigger>
          <TabsTrigger value="trash" data-testid="tab-trash">Trash ({trashedDocs.length})</TabsTrigger>
          <TabsTrigger value="kess" data-testid="tab-kess">Kess ({kessRequests.filter(r => r.status === "requested").length})</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals ({adminReferrals.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments ({paymentsLedger.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit ({auditLogs.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All Users</TabsTrigger>
          {isSuper && <TabsTrigger value="cleanup" data-testid="tab-cleanup">Data Cleanup</TabsTrigger>}
          {isSuper && <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="requests">
          <div className="space-y-6">
            <Section title="Pending Listings" count={pendingListings.length}>
              {pendingListings.length === 0 ? <Empty>No pending listings.</Empty> : (
                <div className="space-y-2">
                  {pendingListings.map((l) => (
                    <div key={l.id} data-testid={`req-listing-${l.id}`} className="bg-white border border-[#D1D5DB] p-4 rounded-sm flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-[#0A192F]">{l.name}</div>
                        <div className="text-xs text-[#4A5568]">{l.provider_name} · {l.city}, {l.state} · {l.housing_type}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" data-testid={`approve-listing-${l.id}`} onClick={() => approveListing(l.id)} className="bg-[#276749] hover:bg-[#22543D]"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                        <Button size="sm" data-testid={`deny-listing-${l.id}`} variant="outline" onClick={() => denyListing(l.id)}><XIcon className="w-3.5 h-3.5 mr-1" /> Deny</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="New Seeker Requests" count={pendingApplicants.length}>
              {pendingApplicants.length === 0 ? <Empty>No pending applicants.</Empty> : (
                <div className="space-y-2">
                  {pendingApplicants.map((i) => (
                    <div key={i.id} data-testid={`req-applicant-${i.id}`} className="bg-white border border-[#D1D5DB] p-4 rounded-sm flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-[#0A192F]">{i.full_name}</div>
                        <div className="text-xs text-[#4A5568]">Urgency: {i.urgency} {i.veteran_status && " · Veteran"}</div>
                      </div>
                      <Button size="sm" data-testid={`review-applicant-${i.id}`} onClick={() => openIntakeVerify(i)} className="bg-[#1E3A8A] hover:bg-[#1A365D]">Review & Verify</Button>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="applications">
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-widest text-[#4A5568]">Filter</Label>
              <select data-testid="app-status-filter" value={appStatusFilter} onChange={(e) => setAppStatusFilter(e.target.value)} className="border border-[#D1D5DB] rounded-sm px-2 py-1 text-sm">
                <option value="">All</option>
                <option value="submitted">Submitted (awaiting review)</option>
                <option value="under_review">Under review</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="draft">Draft (provider-side)</option>
              </select>
              <span className="text-xs text-[#4A5568]">{providerApps.length} shown</span>
            </div>
            {providerApps.length === 0 ? <Empty>No applications match this filter.</Empty> : (
              <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                    <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                      <th className="px-4 py-3">Facility</th>
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Docs</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3 sticky right-0 bg-[#F5F5F0]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D1D5DB]">
                    {providerApps.map((a) => (
                      <tr key={a.id} data-testid={`app-row-${a.id}`}>
                        <td className="px-4 py-3 font-medium">{a.fields?.facility_name || "—"}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{a.applicant?.full_name}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{a.applicant?.email}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{a.fields?.city}{a.fields?.city && a.fields?.state ? ", " : ""}{a.fields?.state}</td>
                        <td className="px-4 py-3">
                          <Badge className={
                            a.status === "approved" ? "bg-[#C6F6D5] text-[#22543D]" :
                            a.status === "denied" ? "bg-[#FED7D7] text-[#742A2A]" :
                            a.status === "submitted" ? "bg-[#BEE3F8] text-[#2C5282]" : "bg-[#FEF6E4] text-[#744210]"
                          }>{a.status.replace(/_/g, ' ')}</Badge>
                        </td>
                        <td className="px-4 py-3 text-[#4A5568]">{(a.documents || []).length}</td>
                        <td className="px-4 py-3 text-xs text-[#4A5568]">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 sticky right-0 bg-white">
                          <Button data-testid={`review-app-${a.id}`} size="sm" className="bg-[#1E3A8A] hover:bg-[#1A365D]" onClick={() => openReview(a.id)}>Review</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="providers">
          <UserTable users={users.filter((u) => u.role === "provider")} onVerify={verify} onGranular={openProvVerify} isSuper={isSuper} onGrant={grantAdmin} onRevoke={revokeAdmin} />
        </TabsContent>

        <TabsContent value="applicants">
          {intakes.length === 0 ? <Empty>No applicant intakes yet.</Empty> : (
            <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                  <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                    <th className="px-4 py-3">Applicant</th>
                    <th className="px-4 py-3">Urgency</th>
                    <th className="px-4 py-3">Veteran</th>
                    <th className="px-4 py-3">Verifications</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D1D5DB]">
                  {intakes.map((i) => (
                    <tr key={i.id} data-testid={`intake-${i.id}`}>
                      <td className="px-4 py-3 font-medium">{i.full_name}</td>
                      <td className="px-4 py-3 capitalize">{i.urgency}</td>
                      <td className="px-4 py-3">{i.veteran_status ? "Yes" : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(i.verifications || {}).filter(([_, v]) => v).map(([k]) => <Badge key={k} variant="outline" className="text-[10px] capitalize">{k}</Badge>)}
                          {Object.values(i.verifications || {}).filter(Boolean).length === 0 && <span className="text-xs text-[#4A5568]">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {i.verified_applicant ? <Badge className="bg-[#C6F6D5] text-[#22543D]"><BadgeCheck className="w-3 h-3 mr-1" /> Verified</Badge> : <Badge variant="outline">Pending</Badge>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" data-testid={`verify-applicant-${i.id}`} onClick={() => openIntakeVerify(i)} className="bg-[#276749] hover:bg-[#22543D]">
                          <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="all">
          <UserTable users={users} onVerify={verify} onGranular={openProvVerify} isSuper={isSuper} onGrant={grantAdmin} onRevoke={revokeAdmin} />
        </TabsContent>

        <TabsContent value="documents">
          {docs.length === 0 ? (
            <Empty>No uploaded documents yet.</Empty>
          ) : (
            <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                  <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                    <th className="px-4 py-3">Document</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Linked</th>
                    <th className="px-4 py-3">Uploaded</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D1D5DB]">
                  {docs.map((d) => {
                    const owner = users.find((u) => u.id === d.user_id);
                    return (
                      <tr key={d.id} data-testid={`admin-doc-${d.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#1E3A8A]" />
                            <div>
                              <div className="font-medium text-[#0A192F]">{d.name}</div>
                              {d.original_filename && <div className="text-[10px] text-[#4A5568]">{d.original_filename} · {Math.round((d.size || 0) / 1024)} KB</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#4A5568]">{owner?.full_name || d.user_id.slice(0, 8)}<div className="text-[10px]">{owner?.role}</div></td>
                        <td className="px-4 py-3 capitalize">{d.document_type.replace('_', ' ')}</td>
                        <td className="px-4 py-3">{d.linked_to ? <Badge variant="outline" className="text-[10px] capitalize">{d.linked_to.replace('_', ' ')}</Badge> : <span className="text-xs text-[#4A5568]">—</span>}</td>
                        <td className="px-4 py-3 text-[#4A5568] text-xs">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          {d.verified ? <Badge className="bg-[#C6F6D5] text-[#22543D]"><BadgeCheck className="w-3 h-3 mr-1" /> Verified</Badge>
                            : d.rejection_reason ? <Badge className="bg-[#FED7D7] text-[#742A2A]">Rejected</Badge>
                            : <Badge variant="outline">Pending</Badge>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <a
                              href="#"
                              onClick={async (e) => {
                                e.preventDefault();
                                try { await openDocument(d.id); }
                                catch (err) { toast.error(err.message); }
                              }}
                              className="text-xs text-[#1E3A8A] underline px-2 py-1"
                              data-testid={`admin-view-doc-${d.id}`}
                            >View</a>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`admin-download-doc-${d.id}`}
                              onClick={() => downloadDocument(d.id)}
                            ><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
                            {!d.verified && (
                              <>
                                <Button size="sm" data-testid={`verify-doc-${d.id}`} onClick={() => verifyDoc(d.id)} className="bg-[#276749] hover:bg-[#22543D]"><Check className="w-3.5 h-3.5 mr-1" /> Verify</Button>
                                <Button size="sm" variant="outline" data-testid={`reject-doc-${d.id}`} onClick={() => rejectDoc(d.id)}><XIcon className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="trash">
          {trashedDocs.length === 0 ? <Empty>No deleted documents.</Empty> : (
            <div className="bg-white border border-[#D1D5DB] rounded-sm divide-y divide-[#D1D5DB]">
              {trashedDocs.map((d) => (
                <div key={d.id} data-testid={`trash-doc-${d.id}`} className="p-4 flex items-center gap-3 justify-between flex-wrap">
                  <div>
                    <div className="font-medium text-[#0A192F]">{d.name}</div>
                    <div className="text-xs text-[#4A5568]">{d.document_type.replace('_',' ')} · Deleted {d.deleted_at ? new Date(d.deleted_at).toLocaleDateString() : "?"}</div>
                  </div>
                  <Button size="sm" data-testid={`restore-doc-${d.id}`} onClick={() => restoreDoc(d.id)} className="bg-[#1E3A8A] hover:bg-[#1A365D]">Restore</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kess">
          <div className="space-y-8">
            <div>
              <div className="font-heading text-sm font-semibold text-[#0A192F] mb-3">Service Catalog (editable)</div>
              <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                    <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                      <th className="px-4 py-3">Service</th>
                      <th className="px-4 py-3">Price ($)</th>
                      <th className="px-4 py-3">Payment link</th>
                      <th className="px-4 py-3">Active</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D1D5DB]">
                    {kessServices.map((s) => (
                      <KessRow key={s.key} svc={s} onSave={updateKessService} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="font-heading text-sm font-semibold text-[#0A192F] mb-3">Service Requests ({kessRequests.length})</div>
              {kessRequests.length === 0 ? <Empty>No requests yet.</Empty> : (
                <div className="bg-white border border-[#D1D5DB] rounded-sm divide-y divide-[#D1D5DB]">
                  {kessRequests.map((r) => (
                    <div key={r.id} data-testid={`kess-req-${r.id}`} className="p-4 flex items-center gap-3 justify-between flex-wrap">
                      <div>
                        <div className="font-medium text-[#0A192F]">{r.service_name} — {r.subject_name}</div>
                        <div className="text-xs text-[#4A5568]">By {r.requested_by_name} ({r.requested_by_role}) · ${r.price?.toFixed?.(2)} · {new Date(r.created_at).toLocaleDateString()}</div>
                        {r.notes && <div className="text-xs text-[#4A5568] mt-1 max-w-xl">{r.notes}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{r.status.replace('_',' ')}</Badge>
                        {r.status === "requested" && <Button size="sm" data-testid={`kess-progress-${r.id}`} onClick={() => updateKessRequest(r.id, "in_progress")} className="bg-[#1E3A8A] hover:bg-[#1A365D]">Start</Button>}
                        {r.status === "in_progress" && <Button size="sm" data-testid={`kess-complete-${r.id}`} onClick={() => updateKessRequest(r.id, "completed")} className="bg-[#276749] hover:bg-[#22543D]">Complete</Button>}
                        {r.status !== "cancelled" && r.status !== "completed" && <Button size="sm" variant="outline" data-testid={`kess-cancel-${r.id}`} onClick={() => updateKessRequest(r.id, "cancelled")}>Cancel</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="referrals">
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-widest text-[#4A5568]">Filter by status</Label>
              <select
                data-testid="ref-status-filter"
                value={refStatusFilter}
                onChange={(e) => setRefStatusFilter(e.target.value)}
                className="border border-[#D1D5DB] rounded-sm px-2 py-1 text-sm"
              >
                <option value="">All</option>
                {["new","submitted","under_review","accepted","waitlisted","placed","completed","rejected","closed"].map(s =>
                  <option key={s} value={s}>{s.replace('_',' ')}</option>
                )}
              </select>
              <span className="text-xs text-[#4A5568]">{adminReferrals.length} shown</span>
            </div>
            {adminReferrals.length === 0 ? <Empty>No referrals match this filter.</Empty> : (
              <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                    <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                      <th className="px-4 py-3">Client</th>
                      <th className="px-4 py-3">Listing</th>
                      <th className="px-4 py-3">Provider</th>
                      <th className="px-4 py-3">Case Manager</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Created</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D1D5DB]">
                    {adminReferrals.map((r) => (
                      <tr key={r.id} data-testid={`admin-ref-${r.id}`}>
                        <td className="px-4 py-3 font-medium">{r.client_name}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{r.listing_name}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{r.provider?.organization || r.provider?.full_name || "—"}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{r.case_manager?.full_name || (r.seeker_id ? "Self-applied" : "—")}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{r.status?.replace('_',' ')}</Badge></td>
                        <td className="px-4 py-3 text-xs text-[#4A5568]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <select
                            data-testid={`ref-override-${r.id}`}
                            defaultValue=""
                            onChange={(e) => { if (e.target.value) { overrideReferral(r.id, e.target.value); e.target.value = ""; } }}
                            className="border border-[#D1D5DB] rounded-sm px-2 py-1 text-xs"
                          >
                            <option value="">Admin override…</option>
                            {["new","submitted","under_review","accepted","waitlisted","placed","completed","rejected","closed"]
                              .filter(s => s !== r.status)
                              .map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap text-xs text-[#4A5568]">
              <span data-testid="payments-total-count">{paymentsLedger.length} transactions</span>
              <span>·</span>
              <span data-testid="payments-paid-count">{paymentsLedger.filter(p => p.payment_status === "paid").length} paid</span>
              <span>·</span>
              <span data-testid="payments-revenue-total">Revenue ${paymentsLedger.filter(p => p.payment_status === "paid").reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toFixed(2)}</span>
            </div>
            {paymentsLedger.length === 0 ? <Empty>No payments have been recorded yet.</Empty> : (
              <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                    <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3">Mode</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Subscription</th>
                      <th className="px-4 py-3">Stripe Session</th>
                      <th className="px-4 py-3">Customer ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D1D5DB]">
                    {paymentsLedger.map((p) => (
                      <tr key={p.id} data-testid={`pay-row-${p.id}`}>
                        <td className="px-4 py-3 text-xs text-[#4A5568]">{(p.created_at || "").slice(0, 10)}</td>
                        <td className="px-4 py-3"><div className="font-medium text-[#0A192F]">{p.user_full_name || "—"}</div><div className="text-xs text-[#4A5568]">{p.user_email}</div></td>
                        <td className="px-4 py-3">{p.plan_name}</td>
                        <td className="px-4 py-3 text-right font-medium">${p.amount}</td>
                        <td className="px-4 py-3 text-[#4A5568] capitalize">{p.mode || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge className={p.payment_status === "paid" ? "bg-[#C6F6D5] text-[#22543D]" : "bg-[#FED7D7] text-[#742A2A]"}>{p.payment_status}</Badge>
                        </td>
                        <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{p.user_subscription_status || "—"}</Badge>{p.user_subscription_tier && <div className="text-xs text-[#4A5568] mt-1">{p.user_subscription_tier}</div>}</td>
                        <td className="px-4 py-3 text-[10px] text-[#4A5568] font-mono break-all max-w-[160px]">{p.session_id || "—"}</td>
                        <td className="px-4 py-3 text-[10px] text-[#4A5568] font-mono break-all max-w-[140px]">{p.user_stripe_customer_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="space-y-4">
            <div className="text-xs text-[#4A5568]">Append-only admin audit ledger. Most recent {auditLogs.length} entries.</div>
            {auditLogs.length === 0 ? <Empty>No audit events recorded yet.</Empty> : (
              <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                    <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                      <th className="px-4 py-3">When</th>
                      <th className="px-4 py-3">Actor</th>
                      <th className="px-4 py-3">Action</th>
                      <th className="px-4 py-3">Target</th>
                      <th className="px-4 py-3">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D1D5DB]">
                    {auditLogs.map((a) => (
                      <tr key={a.id} data-testid={`audit-row-${a.id}`}>
                        <td className="px-4 py-3 text-xs text-[#4A5568]">{(a.ts || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-3"><div className="font-medium">{a.actor_email}</div><div className="text-xs text-[#4A5568]">{a.actor_role}</div></td>
                        <td className="px-4 py-3"><Badge variant="outline">{a.action}</Badge></td>
                        <td className="px-4 py-3 text-xs font-mono text-[#4A5568] max-w-[180px] break-all">{a.target_collection}/{a.target_id}</td>
                        <td className="px-4 py-3 text-xs text-[#742A2A] max-w-[280px]">{a.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {isSuper && (
          <TabsContent value="cleanup">
            <div className="space-y-4">
              <div className="bg-[#FEF6E4] border border-[#C05621]/30 p-4 rounded-sm">
                <div className="font-heading text-sm font-semibold text-[#0A192F]">Test-Data Scan (Read-Only)</div>
                <p className="text-xs text-[#4A5568] mt-1 max-w-2xl">Scans the database for records that look like test data (emails @example.com / @test.com, names prefixed with TEST_, uitest_, etc.). Nothing is deleted. Review before any cleanup action is taken.</p>
                <div className="mt-2 text-[11px] text-[#4A5568] bg-white border border-[#D1D5DB] rounded-sm p-2" data-testid="cleanup-protected-list">
                  <span className="font-semibold text-[#0A192F]">Protected (never deleted):</span> admin@safeharborveterans.org, natasha@safeharborveterans.com, safeharborveteranshousing@gmail.com, info@seniorlovingcaregivers.com, jonesmsa23@myyahoo.com · listings containing &ldquo;Rosehill House&rdquo;, &ldquo;NestBridge Living&rdquo;, &ldquo;Safe Harbor Veterans&rdquo; · any payment_transactions row with status=paid.
                </div>
                <div className="mt-3">
                  <Button data-testid="run-scan-btn" onClick={runScan} disabled={scanLoading} className="bg-[#1E3A8A] hover:bg-[#1A365D]">
                    {scanLoading ? "Working…" : "Run Scan"}
                  </Button>
                  {scanData && ((scanData.totals?.users?.would_delete || 0) + (scanData.totals?.listings?.would_delete || 0) + (scanData.totals?.payment_transactions?.would_delete || 0) > 0) && (
                    <Button
                      data-testid="execute-cleanup-btn"
                      onClick={executeCleanup}
                      disabled={scanLoading}
                      className="ml-3 bg-[#C05621] hover:bg-[#9C4221]"
                    >
                      Execute Cleanup…
                    </Button>
                  )}
                </div>
              </div>

              {scanData && (
                <div className="bg-white border border-[#D1D5DB] rounded-sm p-4" data-testid="scan-results">
                  <div className="font-heading text-sm font-semibold text-[#0A192F] mb-3">Scan Totals</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
                        <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                          <th className="px-3 py-2">Collection</th>
                          <th className="px-3 py-2 text-right">Would Delete</th>
                          <th className="px-3 py-2 text-right">Would Keep</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#D1D5DB]">
                        {Object.entries(scanData.totals || {}).map(([col, v]) => (
                          <tr key={col} data-testid={`scan-row-${col}`}>
                            <td className="px-3 py-2 capitalize">{col.replace('_',' ')}</td>
                            <td className="px-3 py-2 text-right text-[#C05621] font-medium">{v.would_delete}</td>
                            <td className="px-3 py-2 text-right text-[#276749]">{v.would_keep}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="font-heading text-sm font-semibold text-[#0A192F] mt-6 mb-2">Users that would be deleted ({scanData.users?.would_delete?.length || 0})</div>
                  {(scanData.users?.would_delete || []).length === 0 ? (
                    <div className="text-xs text-[#4A5568]">None.</div>
                  ) : (
                    <div className="bg-[#F5F5F0] border border-[#D1D5DB] rounded-sm divide-y divide-[#D1D5DB] max-h-80 overflow-y-auto">
                      {scanData.users.would_delete.map((u) => (
                        <div key={u.id} className="px-3 py-2 text-xs flex items-center justify-between gap-2 flex-wrap">
                          <div>
                            <span className="font-medium">{u.name || "(no name)"}</span>{" "}
                            <span className="text-[#4A5568]">— {u.email} · {u.role}</span>
                          </div>
                          <span className="text-[#4A5568]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-[#4A5568] mt-3">Note: deletion is NOT executed from this UI. Use a manual cleanup script after reviewing this report.</div>
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {isSuper && (
          <TabsContent value="settings">
            <div className="space-y-8">
              <div className="bg-white border border-[#D1D5DB] rounded-sm p-6">
                <h3 className="font-heading text-lg font-semibold text-[#0A192F] mb-4">Contact Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Phone"><Input data-testid="set-phone" value={contact.phone || ""} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></Field>
                  <Field label="Fax"><Input data-testid="set-fax" value={contact.fax || ""} onChange={(e) => setContact({ ...contact, fax: e.target.value })} /></Field>
                  <Field label="Email"><Input data-testid="set-email" value={contact.email || ""} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></Field>
                  <Field label="Facebook"><Input data-testid="set-facebook" value={contact.facebook || ""} onChange={(e) => setContact({ ...contact, facebook: e.target.value })} /></Field>
                  <Field label="Instagram"><Input data-testid="set-instagram" value={contact.instagram || ""} onChange={(e) => setContact({ ...contact, instagram: e.target.value })} /></Field>
                  <Field label="Twitter"><Input data-testid="set-twitter" value={contact.twitter || ""} onChange={(e) => setContact({ ...contact, twitter: e.target.value })} /></Field>
                  <Field label="LinkedIn"><Input data-testid="set-linkedin" value={contact.linkedin || ""} onChange={(e) => setContact({ ...contact, linkedin: e.target.value })} /></Field>
                  <Field label="Address"><Input data-testid="set-address" value={contact.address || ""} onChange={(e) => setContact({ ...contact, address: e.target.value })} /></Field>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button data-testid="save-contact" onClick={saveContact} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white"><Save className="w-4 h-4 mr-2" /> Save Contact</Button>
                </div>
              </div>

              <div className="bg-white border border-[#D1D5DB] rounded-sm p-6">
                <h3 className="font-heading text-lg font-semibold text-[#0A192F] mb-4">Branding</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Logo URL"><Input data-testid="set-logo" value={branding.logo_url || ""} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} placeholder="https://…/logo.png" /></Field>
                  <Field label="QR Code URL"><Input data-testid="set-qr" value={branding.qr_code_url || ""} onChange={(e) => setBranding({ ...branding, qr_code_url: e.target.value })} /></Field>
                  <Field label="Tagline"><Input data-testid="set-tagline" value={branding.tagline || ""} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} /></Field>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button data-testid="save-branding" onClick={saveBranding} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white"><Save className="w-4 h-4 mr-2" /> Save Branding</Button>
                </div>
              </div>

              <div className="bg-[#0A192F] text-white p-6 rounded-sm">
                <h3 className="font-heading text-lg font-semibold">Future: Kess_Maj Labs LLC Integration</h3>
                <p className="text-white/70 text-sm mt-2 max-w-2xl">Income / employment / identity verification, drug testing, alcohol testing, fingerprinting, and background screening modules. Architecturally ready — activate when contracts are signed.</p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  {["Identity Verification", "Income Verification", "Employment Verification", "Drug Testing", "Alcohol Testing", "Fingerprinting", "Background Screening"].map(s => (
                    <Badge key={s} variant="outline" className="border-white/40 text-white/80">{s}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Applicant verification dialog */}
      <Dialog open={!!intakeDialog} onOpenChange={(o) => { if (!o) { setIntakeDialog(null); setIntakeDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Verify Applicant — {intakeDialog?.full_name}</DialogTitle></DialogHeader>

          {intakeDetail && (
            <div className="space-y-4 mb-4" data-testid="intake-detail-body">
              <div className="border border-[#D1D5DB] rounded-sm p-4 bg-[#F5F5F0]">
                <div className="font-heading text-sm font-semibold text-[#0A192F] mb-2">Seeker</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="text-[#4A5568]">Name</div><div>{intakeDetail.seeker?.full_name || "—"}</div>
                  <div className="text-[#4A5568]">Email</div><div>{intakeDetail.seeker?.email || "—"}</div>
                  <div className="text-[#4A5568]">Phone</div><div>{intakeDetail.seeker?.phone || "—"}</div>
                  <div className="text-[#4A5568]">Account Created</div><div>{(intakeDetail.seeker?.created_at || "").slice(0, 10)}</div>
                </div>
              </div>

              <div className="border border-[#D1D5DB] rounded-sm p-4">
                <div className="font-heading text-sm font-semibold text-[#0A192F] mb-2">Intake Form</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="text-[#4A5568]">Readiness</div><div className="capitalize">{intakeDetail.intake?.readiness_status?.replace(/_/g, ' ') || "—"}</div>
                  <div className="text-[#4A5568]">Populations</div><div>{(intakeDetail.intake?.populations || []).join(", ") || "—"}</div>
                  <div className="text-[#4A5568]">Veteran</div><div>{intakeDetail.intake?.veteran ? "Yes" : "No"}</div>
                  <div className="text-[#4A5568]">Preferred City</div><div>{intakeDetail.intake?.preferred_city || "—"}</div>
                  <div className="text-[#4A5568]">Preferred State</div><div>{intakeDetail.intake?.preferred_state || "—"}</div>
                  <div className="text-[#4A5568]">Submitted</div><div>{(intakeDetail.intake?.created_at || "").slice(0, 10)}</div>
                </div>
                {intakeDetail.intake?.notes && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-widest text-[#4A5568] mb-1">Notes</div>
                    <div className="text-sm whitespace-pre-wrap text-[#0A192F]">{intakeDetail.intake.notes}</div>
                  </div>
                )}
              </div>

              <div className="border border-[#D1D5DB] rounded-sm p-4">
                <div className="font-heading text-sm font-semibold text-[#0A192F] mb-2">Uploaded Documents ({intakeDetail.documents.length})</div>
                {intakeDetail.documents.length === 0 ? (
                  <div className="text-xs text-[#4A5568]">This applicant has not uploaded any supporting documents.</div>
                ) : (
                  <div className="divide-y divide-[#E2E8F0]">
                    {intakeDetail.documents.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-3 py-2" data-testid={`intake-doc-${d.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{d.name} <Badge variant="outline" className="ml-1 text-[10px]">{d.document_type || "doc"}</Badge></div>
                          <div className="text-xs text-[#4A5568]">Uploaded {(d.uploaded_at || d.created_at || "").slice(0, 10)}</div>
                        </div>
                        <Button size="sm" variant="outline" data-testid={`download-intake-doc-${d.id}`} onClick={() => downloadDocument(d.id)}>Download</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2 border-t border-[#D1D5DB] pt-4">
            <div className="font-heading text-sm font-semibold text-[#0A192F]">Verification Toggles</div>
            {APPLICANT_VERIFS.map(k => (
              <label key={k} className="flex items-center gap-2 text-sm capitalize">
                <Checkbox data-testid={`verif-app-${k}`} checked={!!intakeVerifs[k]} onCheckedChange={(v) => setIntakeVerifs({ ...intakeVerifs, [k]: !!v })} />
                {k} Verified
              </label>
            ))}
            <div className="text-xs text-[#4A5568] mt-2">Identity + Income required for full &quot;Verified Applicant&quot; badge.</div>
          </div>
          <DialogFooter>
            <Button data-testid="save-applicant-verifs" onClick={saveIntakeVerifs} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider verification dialog */}
      <Dialog open={!!provDialog} onOpenChange={(o) => !o && setProvDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Verify Provider — {provDialog?.organization || provDialog?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {PROVIDER_VERIFS.map(k => (
              <label key={k} className="flex items-center gap-2 text-sm capitalize">
                <Checkbox data-testid={`verif-prov-${k}`} checked={!!provVerifs[k]} onCheckedChange={(v) => setProvVerifs({ ...provVerifs, [k]: !!v })} />
                {k} Verified
              </label>
            ))}
            <div className="text-xs text-[#4A5568] mt-2">All four must be checked for the full "Verified Provider" badge.</div>
          </div>
          <DialogFooter>
            <Button data-testid="save-prov-verifs" onClick={saveProvVerifs} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Application Review Modal */}
      <Dialog open={!!reviewModal} onOpenChange={(v) => !v && setReviewModal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="review-modal">
          {reviewModal && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Review: {reviewModal.application?.fields?.facility_name || "(unnamed)"}
                  <Badge className="ml-2 capitalize">{reviewModal.application?.status?.replace(/_/g, ' ')}</Badge>
                </DialogTitle>
              </DialogHeader>
              <ReviewModalBody data={reviewModal} onDownload={downloadDocument} />
              {["submitted", "under_review", "draft"].includes(reviewModal.application?.status) && (
                <DialogFooter className="gap-2 sticky bottom-0 bg-white pt-3 border-t border-[#D1D5DB] flex-wrap">
                  <Button variant="outline" data-testid="more-info-btn" onClick={() => requestMoreInfo(reviewModal.application.id)} className="text-[#2C5282] border-[#2C5282] hover:bg-[#BEE3F8]">Request More Info</Button>
                  <Button variant="outline" data-testid="deny-app-btn" onClick={() => denyApp(reviewModal.application.id)} className="text-[#742A2A] border-[#742A2A] hover:bg-[#FED7D7]">Reject with Reason</Button>
                  <Button data-testid="approve-app-btn" onClick={() => approveApp(reviewModal.application.id)} className="bg-[#276749] hover:bg-[#22543D] text-white">Approve</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ReviewModalBody = ({ data, onDownload }) => {
  const { application, applicant, documents, all_user_documents = [], intakes = [], listings, payments, version_history } = data;
  const f = application?.fields || {};
  const reg = (applicant && applicant.payment_status) || "—";
  const sub = (applicant && applicant.subscription_status) || "—";
  return (
    <div className="space-y-5 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatusBlock label="Application" value={application?.status} />
        <StatusBlock label="Payment" value={reg} />
        <StatusBlock label="Subscription" value={sub} />
      </div>

      <Card title="Applicant">
        <Row k="Name" v={applicant?.full_name} />
        <Row k="Email" v={applicant?.email} />
        <Row k="Role" v={applicant?.role} />
        <Row k="Phone" v={applicant?.phone} />
        <Row k="Organization (registration)" v={applicant?.organization} />
        <Row k="Account Created" v={(applicant?.created_at || "").slice(0,10)} />
        <Row k="Verified" v={applicant?.verified ? "Yes" : "No"} />
      </Card>

      <Card title="Application Details">
        <Row k="Facility / Organization" v={f.facility_name} />
        <Row k="Contact Person" v={f.contact_person} />
        <Row k="Email" v={f.email} />
        <Row k="Phone" v={f.phone} />
        <Row k="Website" v={f.website} />
        <Row k="Address" v={`${f.address || ""} ${f.city || ""} ${f.state || ""} ${f.zip_code || ""}`.trim()} />
        <Row k="Housing Type" v={f.housing_type} />
        <Row k="Capacity (beds)" v={f.capacity_beds} />
        <Row k="Populations Served" v={(f.populations_served || []).join(", ") || "—"} />
        <Row k="Veteran-Specific Services" v={f.veteran_specific_services} />
        <Row k="Licensing / Certifications" v={f.licensing_certifications} />
        <Row k="Insurance Information" v={f.insurance_information} />
      </Card>

      <Card title={`Uploaded Documents (${documents.length} attached / ${all_user_documents.length} total in account)`}>
        {documents.length === 0 && all_user_documents.length === 0 ? (
          <div className="text-xs text-[#4A5568]">None.</div>
        ) : (
          <>
            {documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 py-1.5 border-b last:border-0 border-[#E2E8F0]" data-testid={`review-doc-${d.id}`}>
                <div>
                  <div className="font-medium">{d.name} <Badge variant="outline" className="ml-1 text-[10px]">{d.document_type || "attached"}</Badge></div>
                  <div className="text-xs text-[#4A5568]">Uploaded {(d.uploaded_at || d.created_at || "").slice(0,10)}</div>
                </div>
                <Button size="sm" variant="outline" data-testid={`download-doc-${d.id}`} onClick={() => onDownload(d.id)}>Download</Button>
              </div>
            ))}
            {all_user_documents.filter(d => !documents.find(a => a.id === d.id)).length > 0 && (
              <div className="mt-3 pt-3 border-t border-dashed border-[#D1D5DB]">
                <div className="text-[10px] uppercase tracking-widest text-[#4A5568] mb-2">Other documents owned by this user (not attached)</div>
                {all_user_documents.filter(d => !documents.find(a => a.id === d.id)).map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 py-1.5 border-b last:border-0 border-[#E2E8F0]">
                    <div>
                      <div className="font-medium">{d.name} <Badge variant="outline" className="ml-1 text-[10px]">{d.document_type || "—"}</Badge></div>
                      <div className="text-xs text-[#4A5568]">Uploaded {(d.uploaded_at || d.created_at || "").slice(0,10)}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => onDownload(d.id)}>Download</Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      <Card title={`Intake Forms (${intakes.length})`}>
        {intakes.length === 0 ? <div className="text-xs text-[#4A5568]">No intake forms submitted by this user.</div> :
          intakes.map((it) => (
            <div key={it.id} className="py-2 border-b last:border-0 border-[#E2E8F0]">
              <div className="font-medium">{it.full_name} <Badge variant="outline" className="ml-1 text-[10px]">{it.readiness_status || "—"}</Badge></div>
              <div className="text-xs text-[#4A5568]">Populations: {(it.populations || []).join(", ") || "—"} · Submitted {(it.created_at || "").slice(0,10)}</div>
              <div className="text-xs text-[#0A192F] mt-1 whitespace-pre-wrap">{it.notes || ""}</div>
            </div>
          ))}
      </Card>

      <Card title={`Linked Listings (${listings.length})`}>
        {listings.length === 0 ? <div className="text-xs text-[#4A5568]">None yet.</div> :
          listings.map((l) => (
            <div key={l.id} className="flex items-center justify-between py-1.5 border-b last:border-0 border-[#E2E8F0]">
              <div>
                <div className="font-medium">{l.name}</div>
                <div className="text-xs text-[#4A5568]">{l.city}, {l.state} · {l.available_beds}/{l.total_beds} beds · {l.approval_status}</div>
              </div>
            </div>
          ))}
      </Card>

      <Card title={`Payment Transactions (${payments.length})`}>
        {payments.length === 0 ? <div className="text-xs text-[#4A5568]">No payment history.</div> :
          payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-1.5 border-b last:border-0 border-[#E2E8F0]">
              <div className="min-w-0">
                <div className="font-medium">{p.plan_name} · ${p.amount}</div>
                <div className="text-xs text-[#4A5568] truncate">{p.session_id} · {p.mode}</div>
              </div>
              <Badge className={p.payment_status === "paid" ? "bg-[#C6F6D5] text-[#22543D]" : "bg-[#FED7D7] text-[#742A2A]"}>{p.payment_status}</Badge>
            </div>
          ))}
      </Card>

      <Card title={`Version History (${version_history.length})`}>
        {version_history.map((v) => (
          <div key={v.id} className="text-xs py-1 border-b last:border-0 border-[#E2E8F0]">
            <span className="font-medium">v{v.version}</span> · {v.status} · {(v.updated_at || "").slice(0,10)} {v.denial_reason && <span className="text-[#742A2A]">— denied: {v.denial_reason}</span>}
          </div>
        ))}
      </Card>

      <Card title="Stage History">
        {(application.stage_history || []).map((h, i) => (
          <div key={`${h.ts || ""}-${h.stage || ""}-${i}`} className="text-xs py-1 border-b last:border-0 border-[#E2E8F0]">
            <span className="capitalize font-medium">{h.stage}</span> · {(h.ts || "").slice(0,16).replace("T"," ")} · {h.notes}
          </div>
        ))}
      </Card>
    </div>
  );
};
const Card = ({ title, children }) => (
  <div className="border border-[#D1D5DB] rounded-sm p-4">
    <div className="font-heading text-sm font-semibold text-[#0A192F] mb-2">{title}</div>
    {children}
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <div className="text-[#4A5568]">{k}</div>
    <div className="text-[#0A192F] text-right max-w-[60%] break-words">{v || "—"}</div>
  </div>
);
const StatusBlock = ({ label, value }) => (
  <div className="bg-[#F5F5F0] border border-[#D1D5DB] rounded-sm p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#4A5568]">{label}</div>
    <div className="font-medium capitalize mt-1">{(value || "—").toString().replace(/_/g, ' ')}</div>
  </div>
);

const Stat = ({ title, value, sub, accent }) => (
  <div className="bg-white border border-[#D1D5DB] p-4 rounded-sm">
    <div className="text-xs uppercase tracking-widest text-[#4A5568]">{title}</div>
    <div className={`font-heading text-3xl font-bold mt-1 ${accent ? "text-[#C05621]" : "text-[#0A192F]"}`}>{value}</div>
    {sub && <div className="text-xs text-[#4A5568] mt-0.5">{sub}</div>}
  </div>
);

const Field = ({ label, children }) => (<div><Label className="block mb-1.5">{label}</Label>{children}</div>);
const Empty = ({ children }) => <div className="border border-dashed border-[#D1D5DB] p-8 text-center bg-white rounded-sm text-sm text-[#4A5568]">{children}</div>;
const Section = ({ title, count, children }) => (
  <div>
    <div className="font-heading text-sm font-semibold text-[#0A192F] mb-3">{title} <span className="text-[#4A5568] font-normal">({count})</span></div>
    {children}
  </div>
);

const KessRow = ({ svc, onSave }) => {
  const [price, setPrice] = useState(svc.price);
  const [link, setLink] = useState(svc.payment_link || "");
  const [active, setActive] = useState(svc.active !== false);
  return (
    <tr data-testid={`kess-row-${svc.key}`}>
      <td className="px-4 py-3 font-medium">{svc.name}</td>
      <td className="px-4 py-3"><Input type="number" step="0.01" data-testid={`kess-price-${svc.key}`} value={price} onChange={(e) => setPrice(parseFloat(e.target.value || "0"))} className="max-w-[110px]" /></td>
      <td className="px-4 py-3"><Input data-testid={`kess-link-${svc.key}`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" /></td>
      <td className="px-4 py-3"><label className="flex items-center gap-2 text-xs"><Checkbox checked={active} onCheckedChange={(v) => setActive(!!v)} /> Active</label></td>
      <td className="px-4 py-3 text-right"><Button size="sm" data-testid={`kess-save-${svc.key}`} onClick={() => onSave(svc.key, { price, payment_link: link, active })} className="bg-[#1E3A8A] hover:bg-[#1A365D]">Save</Button></td>
    </tr>
  );
};

const UserTable = ({ users, onVerify, onGranular, isSuper, onGrant, onRevoke }) => {
  const statusPill = (label, kind) => {
    const map = {
      paid: "bg-[#C6F6D5] text-[#22543D]",
      unpaid: "bg-[#FED7D7] text-[#742A2A]",
      complete: "bg-[#C6F6D5] text-[#22543D]",
      incomplete: "bg-[#FEF6E4] text-[#744210]",
      never_started: "bg-[#E2E8F0] text-[#4A5568]",
      active: "bg-[#C6F6D5] text-[#22543D]",
      inactive: "bg-[#E2E8F0] text-[#4A5568]",
      canceled: "bg-[#FED7D7] text-[#742A2A]",
      past_due: "bg-[#FED7D7] text-[#742A2A]",
      pending_payment: "bg-[#FED7D7] text-[#742A2A]",
      pending_review: "bg-[#FEF6E4] text-[#744210]",
      approved: "bg-[#C6F6D5] text-[#22543D]",
      registered: "bg-[#BEE3F8] text-[#2C5282]",
    };
    const cls = map[kind] || "bg-[#E2E8F0] text-[#4A5568]";
    return <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-medium ${cls}`}>{label}</span>;
  };
  return (
  <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b border-[#D1D5DB] bg-[#F5F5F0]">
        <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
          <th className="px-4 py-3">Name</th>
          <th className="px-4 py-3">Email</th>
          <th className="px-4 py-3">Role</th>
          <th className="px-4 py-3">Plan</th>
          <th className="px-4 py-3">Payment</th>
          <th className="px-4 py-3">Checkout</th>
          <th className="px-4 py-3">Subscription</th>
          <th className="px-4 py-3">Approval</th>
          <th className="px-4 py-3">Verified</th>
          <th className="px-4 py-3 sticky right-0 bg-[#F5F5F0]">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#D1D5DB]">
        {users.map((u) => (
          <tr key={u.id} data-testid={`admin-user-${u.id}`}>
            <td className="px-4 py-3 font-medium">{u.full_name}</td>
            <td className="px-4 py-3 text-[#4A5568]">{u.email}</td>
            <td className="px-4 py-3"><Badge variant="outline">{u.role.replace('_', ' ')}</Badge></td>
            <td className="px-4 py-3 text-[#4A5568]">{u.subscription_tier || "—"}</td>
            <td className="px-4 py-3" data-testid={`payment-status-${u.id}`}>
              {u.role === "provider" ? statusPill((u.payment_status || "unpaid").replace('_',' '), u.payment_status || "unpaid") : <span className="text-xs text-[#4A5568]">—</span>}
            </td>
            <td className="px-4 py-3" data-testid={`checkout-status-${u.id}`}>
              {u.role === "provider" ? statusPill((u.checkout_status || "never_started").replace('_',' '), u.checkout_status || "never_started") : <span className="text-xs text-[#4A5568]">—</span>}
            </td>
            <td className="px-4 py-3" data-testid={`subscription-status-${u.id}`}>
              {u.role === "provider" || u.role === "case_manager" || u.role === "agency_owner"
                ? statusPill((u.subscription_status || "inactive").replace('_',' '), u.subscription_status || "inactive")
                : <span className="text-xs text-[#4A5568]">—</span>}
            </td>
            <td className="px-4 py-3" data-testid={`approval-status-${u.id}`}>
              {u.role === "provider" ? statusPill((u.approval_status || "registered").replace('_',' '), u.approval_status || "registered") : <span className="text-xs text-[#4A5568]">—</span>}
            </td>
            <td className="px-4 py-3">
              {u.verified ? <Badge className="bg-[#C6F6D5] text-[#22543D]">Verified</Badge> : <Badge variant="outline">Unverified</Badge>}
            </td>
            <td className="px-4 py-3 text-right sticky right-0 bg-white">
              <div className="flex justify-end gap-1 flex-wrap">
                {u.role === "provider" && (
                  <Button size="sm" variant="outline" data-testid={`granular-${u.id}`} onClick={() => onGranular(u)}>Verify Granular</Button>
                )}
                {u.role !== "admin" && u.role !== "super_admin" && (
                  <Button size="sm" variant={u.verified ? "outline" : "default"} className={u.verified ? "" : "bg-[#276749] hover:bg-[#22543D]"} data-testid={`verify-${u.id}`} onClick={() => onVerify(u.id, u.verified)}>
                    {u.verified ? <><ShieldOff className="w-3.5 h-3.5 mr-1" /> Unverify</> : <><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verify</>}
                  </Button>
                )}
                {isSuper && u.role !== "super_admin" && (
                  u.role === "admin"
                    ? <Button size="sm" variant="outline" data-testid={`revoke-${u.id}`} onClick={() => onRevoke(u.id)}>Revoke Admin</Button>
                    : <Button size="sm" variant="outline" data-testid={`grant-${u.id}`} onClick={() => onGrant(u.id)}>Make Admin</Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};
